import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/constants";
import { Search, Mail, Phone, FolderKanban, Building2, User as UserIcon, Star, AlertTriangle } from "lucide-react";

export default function ClientDirectory() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [reviewOnly, setReviewOnly] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || []))
      .catch((e) => toast.error(apiErr(e, "Could not load clients")))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return clients.filter((c) => {
      if (cat !== "all" && c.category !== cat) return false;
      if (reviewOnly && !c.needs_review) return false;
      if (s && ![c.display_name, c.email, c.phone, c.project_name, c.builder]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [clients, q, cat, reviewOnly]);

  const reviewCount = clients.filter((c) => c.needs_review).length;

  return (
    <div className="p-6 lg:p-10 max-w-[1100px]">
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter">Clients</h1>
        <p className="text-zinc-500 mt-2 text-sm">{clients.length} client{clients.length !== 1 ? "s" : ""} across all projects</p>
      </header>

      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client, project, email or phone…"
          className="pl-9 rounded-sm" data-testid="client-search" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[{ k: "all", label: "All" }, ...CATEGORY_ORDER.map((c) => ({ k: c, label: `${CATEGORY_META[c]?.label} ${CATEGORY_META[c]?.sub}` }))].map((c) => (
          <button key={c.k} onClick={() => setCat(c.k)} data-testid={`client-filter-${c.k}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${cat === c.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
            {c.label}
          </button>
        ))}
        {reviewCount > 0 && (
          <button onClick={() => setReviewOnly((v) => !v)} data-testid="client-filter-review"
            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${reviewOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Needs review ({reviewCount})
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center text-zinc-500">No clients found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const isCompany = c.client_type === "company";
            const Icon = isCompany ? Building2 : UserIcon;
            return (
              <button key={c.client_id} onClick={() => navigate(`/project/${c.project_id}`)}
                className="text-left bg-white border border-zinc-200 rounded-sm p-4 hover:border-zinc-400 hover:shadow-sm transition-all" data-testid={`client-card-${c.client_id}`}>
                <div className="flex items-center gap-3">
                  {c.logo_path ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.logo_path}`} alt="" className="h-12 w-12 rounded-sm object-contain border border-zinc-100 shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-sm bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0"><Icon className="h-5 w-5" /></div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold truncate">{c.display_name}</p>
                      {c.primary && <Star className="h-3 w-3 fill-zinc-900 text-zinc-900 shrink-0" />}
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{isCompany ? "Company" : "Individual"}</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-sm text-zinc-600">
                  <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-zinc-400 shrink-0" />{c.email || <span className="text-zinc-400">No email</span>}</p>
                  <p className="flex items-center gap-2 truncate"><Phone className="h-3.5 w-3.5 text-zinc-400 shrink-0" />{c.phone || <span className="text-zinc-400">No phone</span>}</p>
                  <p className="flex items-center gap-2 text-xs text-zinc-500 truncate"><FolderKanban className="h-3.5 w-3.5 shrink-0" />{c.project_name}{c.builder ? ` · ${c.builder}` : ""}</p>
                </div>
                {c.needs_review && (
                  <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded-sm px-2 py-0.5 mt-2" data-testid={`client-review-${c.client_id}`}>
                    <AlertTriangle className="h-3 w-3" /> Imported · needs review
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
